import { inject, Injectable } from '@angular/core';
import { DoodProductModel, IDeclinableCategory } from '@core/models/product.model';
import { DeclinableProductCart } from '@shared/local-store/cart/cart-draft-declinable-product.store';
import { ProductsService } from '@core/services/products/products.service';
import { combineLatest, map, Observable, take } from 'rxjs';
import { StepInfo } from '@core/models/stepper.model';
import { SimpleProductCart } from '../cart/cart-draft-simple-product.store';
import { IAdditionGroup } from '@core/models/shop.model';
import { ProductStoreRefiner } from '@common/refiners/product.refiner';
import { getAdditionGroupPosition } from './util/stepper-util';
import { ICartDeclinableAdditionsItem } from '@core/models/cart.model';
import { AutoFillCartItemRulesAdditionsGroup } from '../cart/auto-fill-cart-item-rules/auto-fill-cart-item-rules-additions-group';
import { AutoFillCartItemRulesCategory } from '../cart/auto-fill-cart-item-rules/auto-fill-cart-item-rules-category';

type StepPosition =
  | {
      categoryPosition: number;
      additionGroupPosition: number | undefined;
    }
  | {
      additionGroupPosition: number;
    };

type StepInfoWithIndex = StepInfo & StepPosition;

type ProductStepInfo = StepInfoWithIndex & {
  id: string;
  quantity: number;
  /** False when the item is locked in the menu and has no stepper step. */
  navigable?: boolean;
};

export type ProductStepDetails = ProductStepInfo & {
  product: DoodProductModel | undefined;
};

@Injectable({
  providedIn: 'root',
})
export class CompositionStepperUtil {
  private readonly productsService = inject(ProductsService);
  private readonly productStoreRefiner = inject(ProductStoreRefiner);

  public declinableProductStepperComposition$({
    categories,
    cart,
  }: {
    categories: IDeclinableCategory[];
    cart: DeclinableProductCart;
  }): Observable<ProductStepDetails[] | undefined> {
    const cartWithSimpleProductGroupedQuantity = {
      ...cart,
      products: cart.products?.reduce((acc, item) => {
        const existingItem = acc.find(i => i.id === item.id);
        if (existingItem) {
          existingItem.quantity += item.quantity;
        } else {
          acc.push({ ...item });
        }
        return acc;
      }, [] as ICartDeclinableAdditionsItem[]),
    };
    return combineLatest({
      allProducts: this.productsService.selectAll$(),
      additionsGroups: this.productStoreRefiner.selectAdditionsGroups,
    }).pipe(
      take(1),
      map(({ allProducts, additionsGroups }) => {
        return (
          this.declinableSelectableProductWithQuantityIds({
            cart: cartWithSimpleProductGroupedQuantity,
            categories,
            allProducts,
            additionsGroups,
          })
            ?.map(
              selectedProductData =>
                ({
                  product: allProducts.find(allProduct => allProduct.id === selectedProductData.id),
                  ...selectedProductData,
                }) satisfies ProductStepDetails,
            )
            .map(data => {
              const result = {
                data,
                categoryPosition: categories.findIndex(category => {
                  return category.store_id === data.categoryId;
                }),
                simpleProductId: data.simpleProductId,
                additionGroupPosition: allProducts
                  .find(product => product.id === data.simpleProductId)
                  ?.additions?.products.findIndex(id => id === data.id),
              };

              return result;
            })
            .sort((a, b) => {
              if (a.categoryPosition === b.categoryPosition) {
                return (a.additionGroupPosition ?? 0) - (b.additionGroupPosition ?? 0);
              }

              return a.categoryPosition - b.categoryPosition;
            })
            .map(({ data }) => data) ?? []
        );
      }),
    );
  }

  private declinableSelectableProductWithQuantityIds({
    cart,
    categories,
    allProducts,
    additionsGroups,
  }: {
    cart: DeclinableProductCart;
    categories: IDeclinableCategory[];
    allProducts: DoodProductModel[];
    additionsGroups: IAdditionGroup[];
  }): ProductStepInfo[] | undefined {
    const productsIdToDisplay = cart.products
      ?.map(item => {
        const category = categories.find(candidate =>
          candidate.products.some(categoryProduct => categoryProduct === item.id),
        );
        const product = allProducts.find(candidate => candidate.id === item.id);
        const navigable = AutoFillCartItemRulesCategory.isKioskCompositionNavigable({
          category,
          hasAdditions: CompositionStepperUtil.hasNavigableAdditions({
            product,
            allProducts,
            additionGroups: additionsGroups,
          }),
        });
        const additionIdWithQuantity =
          item.separate_additions_by_group
            ?.map(group => {
              const countByAdditionId =
                group.selected.reduce<Record<string, number>>((acc, id) => {
                  acc[id] = (acc[id] || 0) + 1;
                  return acc;
                }, {}) ?? {};
              const product = allProducts.find(product => product.id === item.id);

              const additionGroupPosition = getAdditionGroupPosition({
                product,
                allProducts,
                groupId: group.id,
                additionsGroups,
              });
              return Object.entries(countByAdditionId).flatMap(([id, quantity]) => {
                if (
                  CompositionStepperUtil.isExclusiveAdditionStep({
                    groupId: group.id,
                    product,
                    allProducts,
                    additionGroups: additionsGroups,
                  })
                ) {
                  return [];
                }
                // ! additionGroupPosition can be 0, so in this case check the order of the additionProductId from the simpleproduct additions
                const stepInfo: StepInfoWithIndex = {
                  categoryPosition:
                    categories.findIndex(category => category.store_id === item.store_id) ?? -1,
                  additionGroupPosition,
                  categoryId: item.store_id,
                  simpleProductId: item.id,
                  additionsGroupId: group.id,
                };
                return [
                  {
                    id,
                    quantity,
                    ...stepInfo,
                  },
                ];
              });
            })
            .flat() ?? [];
        const stepInfo: StepInfoWithIndex = {
          categoryId: item.store_id,
          categoryPosition:
            categories.findIndex(category => category.store_id === item.store_id) ?? -1,
          additionGroupPosition: undefined, // todoR  check if we need to set, on black burger menu
          // simpleProductId: item.id,
        };
        return [
          [
            {
              id: item.id,
              quantity: item.quantity,
              navigable,
              ...stepInfo,
            },
          ],
          ...additionIdWithQuantity,
        ];
      })
      .flat();
    const result = productsIdToDisplay?.flat();
    return result;
  }

  public simpleProductStepperComposition$({
    cart,
    productAdditionIds,
  }: {
    cart: SimpleProductCart;
    productAdditionIds: string[];
  }): Observable<ProductStepDetails[] | undefined> {
    return combineLatest({
      allProducts: this.productsService.selectAll$(),
      additionsGroups: this.productStoreRefiner.selectAdditionsGroups,
    }).pipe(
      take(1),
      map(({ allProducts, additionsGroups }) => {
        const product = allProducts.find(candidate => candidate.id === cart.item_id);
        const result =
          this.simpleProductSelectableStepsWithProductWithQuantity({
            cart,
            productAdditionIds,
            product,
            allProducts,
            additionGroups: additionsGroups,
          })
            ?.map(selectedProductData => ({
              product: allProducts.find(allProduct => allProduct.id === selectedProductData.id),
              ...selectedProductData,
            }))
            .map(data => ({
              data,
              additionGroupPosition: productAdditionIds.findIndex(id => id === data.id),
            }))
            .sort((a, b) => (a.additionGroupPosition ?? 0) - (b.additionGroupPosition ?? 0))
            .map(({ data }) => data) ?? [];
        return result;
      }),
    );
  }

  private static hasNavigableAdditions({
    product,
    allProducts,
    additionGroups,
  }: {
    product: DoodProductModel | undefined;
    allProducts: DoodProductModel[];
    additionGroups: IAdditionGroup[];
  }) {
    if (!product) {
      return false;
    }
    const groupIds = [
      ...new Set(
        product.additions.products
          .map(
            additionId =>
              allProducts.find(candidate => candidate.id === additionId)?.addition_group,
          )
          .filter((groupId): groupId is string => !!groupId),
      ),
    ];
    return groupIds.some(
      groupId =>
        !CompositionStepperUtil.isExclusiveAdditionStep({
          groupId,
          product,
          allProducts,
          additionGroups,
        }),
    );
  }

  private static isExclusiveAdditionStep({
    groupId,
    product,
    allProducts,
    additionGroups,
  }: {
    groupId: string;
    product: DoodProductModel | undefined;
    allProducts: DoodProductModel[];
    additionGroups: IAdditionGroup[];
  }) {
    const source = additionGroups.find(additionGroup => additionGroup.id === groupId);
    if (!source || !product) {
      return false;
    }
    const items = product.additions.products
      .map(additionId => allProducts.find(candidate => candidate.id === additionId))
      .filter(
        (addition): addition is DoodProductModel =>
          !!addition && addition.addition_group === groupId,
      );
    if (items.length === 0) {
      return false;
    }
    return AutoFillCartItemRulesAdditionsGroup.hasExclusiveAdditionInAdditionsGroup({
      additionGroup: {
        ...source,
        items,
      },
    });
  }

  private simpleProductSelectableStepsWithProductWithQuantity({
    cart,
    productAdditionIds,
    product,
    allProducts,
    additionGroups,
  }: {
    cart: SimpleProductCart;
    productAdditionIds: string[];
    product: DoodProductModel | undefined;
    allProducts: DoodProductModel[];
    additionGroups: IAdditionGroup[];
  }): ProductStepInfo[] | undefined {
    const additionIdWithQuantity =
      cart.separate_additions_by_group
        ?.map(group => {
          const countByAdditionId =
            group.selected.reduce<Record<string, number>>((acc, id) => {
              acc[id] = (acc[id] || 0) + 1;
              return acc;
            }, {}) ?? {};
          return Object.entries(countByAdditionId).flatMap(([id, quantity]) => {
            if (
              CompositionStepperUtil.isExclusiveAdditionStep({
                groupId: group.id,
                product,
                allProducts,
                additionGroups,
              })
            ) {
              return [];
            }
            const stepInfo: StepInfoWithIndex = {
              simpleProductId: cart.item_id,
              additionsGroupId: group.id,
              additionGroupPosition: productAdditionIds.findIndex(id => id === group.id),
            };
            return [
              {
                id,
                quantity,
                ...stepInfo,
              },
            ];
          });
        })
        .flat() ?? [];
    return additionIdWithQuantity;
  }
}
